// **画面（DOM の組み立てと描き換え）だけを持つ**。接続・音声・制御には触らない
// （配線は `main.ts`。分ける理由は DESIGN-0006 §3-2 = このプラットフォーム層を捨てるときに
// 配線だけ残せるようにするため）。
//
// **何を出すかの正本は `client/画面仕様.md`。** 画面は 4 つ（入室前・通話中・設定・この通話について。
// 2026-10-21 のユーザー判断で「1 画面だけに限らなくてよい」となった）。
// **文言に補足を付けない**（同 §6-3）ので、ここに書く文字列は短いものだけ。

import type { ChannelRow, Phase, PersonRow } from "./app-state.ts";
import { muteLabel, statusText } from "./app-state.ts";
import type { Notice } from "./failure-notice.ts";

/** どの画面を出しているか。 */
export type ScreenName = "home" | "call" | "settings" | "about";

/** 画面から配線へ渡す出来事。**押された・動かされたという事実だけを渡す。** */
export interface ScreenHandlers {
  onSelectChannel(id: string): void;
  onJoin(): void;
  /** 「新しく作る」。表示名は画面で検査済み（ADR-0022 §1-2）。 */
  onCreate(displayName: string): void;
  /**
   * **新しいグループを作る**（ADR-0024）。コードはサーバが採番する。
   *
   * 入れるのは**名前 1 つだけ**で、それがグループと最初のチャンネルの表示名になる。
   */
  onCreateGroup(displayName: string): void;
  onLeave(): void;
  onRetry(): void;
  onSelectGroup(group: string): void;
  /** 前にいた場所を端末の履歴から消す（**チャンネルは消えない**。板 #220）。 */
  onForget(id: string): void;
  onName(name: string): void;
  onMute(muted: boolean): void;
  onMasterGainDb(db: number): void;
  onSendGainDb(db: number): void;
  /** 参加者ごとの受信音量。`commit` はつまみを離したとき（サーバへ送るのはそのときだけ）。 */
  onPersonGainDb(id: number, db: number, commit: boolean): void;
  /** 送る音の範囲。`commit` はつまみを離したとき。 */
  onBandHz(highpassHz: number, lowpassHz: number, commit: boolean): void;
  onCopyInvite(): void;
  /**
   * **いま選んでいるグループの招待 URL をコピーする**（2026-10-29）。
   *
   * チャンネル宛の {@link ScreenHandlers.onCopyInvite} と別物。**こちらは入室前の画面**で、
   * 行き先を決め打ちせずにグループへ招く（`#group=<コード>`）。
   */
  onCopyGroupInvite(): void;
  onSaveLog(): void;
}

const RECEIVE_GAIN_MIN_DB = -20;
const RECEIVE_GAIN_MAX_DB = 20;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (text !== undefined) e.textContent = text;
  return e;
}

function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const b = el("button", className, label);
  b.type = "button";
  b.addEventListener("click", onClick);
  return b;
}

/** つまみ 1 本（名前・いまの値・範囲）。 */
interface Slider {
  field: HTMLDivElement;
  input: HTMLInputElement;
  value: HTMLSpanElement;
}

function slider(label: string, min: number, max: number, step: number, unit: string): Slider {
  const field = el("div", "field");
  const lab = el("label");
  const name = el("span", undefined, label);
  const value = el("span", "v");
  const input = el("input");
  input.type = "range";
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.setAttribute("aria-label", label);
  lab.append(name, value);
  field.append(lab, input);
  const show = () => {
    value.textContent = `${input.value} ${unit}`.trim();
  };
  input.addEventListener("input", show);
  show();
  return { field, input, value };
}

/** dB を 0〜100% に直す（ゲージの描画用。−60 dB を左端にする）。 */
function dbToPercent(db: number): number {
  const p = ((db + 60) / 60) * 100;
  return Math.max(0, Math.min(100, p));
}

export class Screen {
  /** 検証の欄の入れ物（設定の画面の中。`mountDiagnostics` に渡す。板 #90 で外す）。 */
  readonly diagnostics = el("div");

  private readonly screens: Record<ScreenName, HTMLElement>;
  private readonly header: HTMLElement;
  private readonly badge = el("span", "badge");
  private readonly nameInput = el("input");
  private readonly groups = el("div", "chips");
  private readonly list = el("div", "list");
  private readonly joinButton: HTMLButtonElement;
  private readonly createButton: HTMLButtonElement;
  private readonly createGroupButton: HTMLButtonElement;
  private readonly createBox = el("div", "field");
  private readonly createInput = el("input");
  private readonly createError = el("p", "notice bad");
  private readonly homeNotice = el("p", "notice bad");
  private readonly groupInviteButton: HTMLButtonElement;
  private readonly groupInviteUrl = el("p", "url");
  private readonly groupCopyFeedback = el("p", "note");
  private readonly callTitle = el("span", "title", "通話");
  private readonly callNotice = el("p", "notice warn");
  private readonly muteButton: HTMLButtonElement;
  private readonly people = el("div", "people");
  private readonly logLine = el("p", "log");
  private readonly copyFeedback = el("p", "note");
  private readonly inviteUrl = el("p", "url");
  private readonly leaveButton: HTMLButtonElement;
  private readonly retryButton: HTMLButtonElement;
  private readonly master: Slider;
  private readonly send: Slider;
  private readonly gauge = el("div", "gauge");
  private readonly gaugeFloor = el("div", "floor");
  private readonly gaugeLevel = el("div", "level");
  private readonly gaugeThreshold = el("div", "thr");
  private readonly highpass: Slider;
  private readonly lowpass: Slider;
  private readonly versionLine = el("p", "note");
  private selected = "";

  constructor(
    private readonly root: HTMLElement,
    private readonly h: ScreenHandlers,
  ) {
    this.joinButton = button("入室", "btn primary", () => this.h.onJoin());
    this.createButton = button("＋ 新しく作る", "btn", () => this.openCreate("channel"));
    // **グループを作る**（ADR-0024）。所属が 1 つも無い端末の入口でもある
    this.createGroupButton = button("＋ グループを作る", "btn", () => this.openCreate("group"));
    // **グループの招待 URL**（2026-10-29）。**入室前に置く** — グループへ人を招くのは
    // 通話を始める前の行為なので、通話中の「招待 URL をコピー」（チャンネル宛）とは別
    this.groupInviteButton = button("グループの招待 URL をコピー", "btn ghost", () =>
      this.h.onCopyGroupInvite(),
    );
    this.muteButton = button(muteLabel(false), "btn mute", () => {
      const next = this.muteButton.getAttribute("aria-pressed") !== "true";
      this.h.onMute(next);
    });
    this.leaveButton = button("退室", "btn danger", () => this.h.onLeave());
    this.retryButton = button("繋ぎ直す", "btn", () => this.h.onRetry());
    this.master = slider("聞く音の大きさ", RECEIVE_GAIN_MIN_DB, RECEIVE_GAIN_MAX_DB, 1, "dB");
    this.send = slider("送る声の大きさ", -20, 20, 1, "dB");
    // 送る音の範囲は**設定の画面に置く**（2026-10-21 のユーザー判断）
    this.highpass = slider("送る音の範囲（低音）", 20, 500, 10, "Hz");
    this.lowpass = slider("送る音の範囲（高音）", 1000, 3400, 100, "Hz");

    this.screens = {
      home: this.buildHome(),
      call: this.buildCall(),
      settings: this.buildSettings(),
      about: this.buildAbout(),
    };
    this.header = this.buildHeader();
    this.root.replaceChildren(this.header, ...Object.values(this.screens));
    this.show("home");
    this.setNotice(null);
    this.setCopyFeedback("");
    this.setGroupInviteUrl("");
    this.setGroupCopyFeedback("");
    this.closeCreate();
    this.callNotice.hidden = true;
    this.logLine.hidden = true;
  }

  // ---- 組み立て ----------------------------------------------------------

  /** 入室前と通話中で使い回す見出し（節点は 1 か所にしか置けないので、画面ごとに作らない）。 */
  private buildHeader(): HTMLElement {
    const bar = el("div", "bar");
    bar.append(this.callTitle, this.badge);
    return bar;
  }

  private buildHome(): HTMLElement {
    const s = el("section");
    const meBar = el("div", "bar");
    this.nameInput.type = "text";
    this.nameInput.maxLength = 32;
    this.nameInput.setAttribute("aria-label", "あなたの名前");
    this.nameInput.addEventListener("change", () => this.h.onName(this.nameInput.value.trim()));
    meBar.append(this.nameInput);

    this.createInput.type = "text";
    this.createInput.maxLength = 64;
    this.createInput.setAttribute("aria-label", "チャンネルの名前");
    this.createLabel.append(this.createLabelText);
    const createRow = el("div", "row2");
    createRow.append(
      button("作って入室", "btn primary", () => {
        const name = this.createInput.value.trim();
        if (this.creating === "group") this.h.onCreateGroup(name);
        else this.h.onCreate(name);
      }),
      button("やめる", "btn ghost", () => this.closeCreate()),
    );
    this.createBox.append(this.createLabel, this.createInput, this.createError, createRow);

    const open = el("div", "row2");
    open.append(
      button("設定", "btn ghost", () => this.show("settings")),
      button("この通話について", "btn ghost", () => this.show("about")),
    );
    s.append(
      meBar,
      this.groups,
      this.list,
      this.createButton,
      this.createGroupButton,
      this.createBox,
      this.joinButton,
      this.homeNotice,
      this.groupInviteButton,
      this.groupCopyFeedback,
      this.groupInviteUrl,
      el("p", "notice", "ヘッドセットをお使いください"),
      open,
    );
    return s;
  }

  private buildCall(): HTMLElement {
    const s = el("section");
    const left = el("div");
    left.append(this.muteButton, this.people, this.logLine);
    const right = el("div");
    right.append(this.master.field, this.send.field);

    const cols = el("div", "cols");
    cols.append(left, right);

    const invite = el("div");
    invite.append(
      button("招待 URL をコピー", "btn", () => this.h.onCopyInvite()),
      this.copyFeedback,
      this.inviteUrl,
    );
    const foot = el("div", "row2");
    foot.append(
      button("設定", "btn ghost", () => this.show("settings")),
      this.retryButton,
      this.leaveButton,
    );
    s.append(this.callNotice, cols, invite, foot);
    return s;
  }

  private buildSettings(): HTMLElement {
    const s = el("section");
    const bar = el("div", "bar");
    bar.append(el("span", "title", "設定"), button("戻る", "btn ghost", () => this.back()));

    // 音声検出のゲージ（2026-10-21 のユーザー判断でここだけに置く）
    this.gauge.append(this.gaugeFloor, this.gaugeLevel, this.gaugeThreshold);
    const gaugeField = el("div", "field");
    const gaugeLabel = el("label");
    gaugeLabel.append(el("span", undefined, "音声検出"));
    gaugeField.append(gaugeLabel, this.gauge);

    const save = button("ログをテキストに保存", "btn ghost", () => this.h.onSaveLog());
    // **検証の欄は折りたたんで残す**（利用者に見せない位置。板 #90 で外す）
    const details = el("details");
    details.append(el("summary", undefined, "検証"), this.diagnostics);

    s.append(
      bar,
      gaugeField,
      this.highpass.field,
      this.lowpass.field,
      save,
      details,
      this.versionLine,
      button("この通話について", "btn ghost", () => this.show("about")),
    );
    return s;
  }

  private buildAbout(): HTMLElement {
    const s = el("section");
    const bar = el("div", "bar");
    bar.append(el("span", "title", "この通話について"), button("戻る", "btn ghost", () => this.back()));
    const table = el("table");
    // 画面仕様 §8。**この画面は最終的に消す**（2026-10-21 の見立て）
    const rows: [string, string][] = [
      ["映像", "使えません"],
      ["録音", "しません"],
      ["ログ", "接続の記録をサーバへ送ります。音声は送りません"],
      ["合言葉", "ありません。URL を知っている人が入れます"],
      ["マイク", "ヘッドセットを勧めます"],
      ["スマホ", "画面を点けたままにしてください"],
      ["更新", "読み込み直すと新しくなります"],
    ];
    for (const [k, v] of rows) {
      const tr = el("tr");
      tr.append(el("th", undefined, k), el("td", undefined, v));
      table.append(tr);
    }
    s.append(bar, table);
    return s;
  }

  // ---- 画面の出し入れ ----------------------------------------------------

  /** どの画面を出すか。**入室前と通話中は配線が決める**（状態で変わる）。 */
  show(name: ScreenName): void {
    for (const [key, node] of Object.entries(this.screens)) node.hidden = key !== name;
    // 設定と「この通話について」は自分の見出しを持つので、共通の見出しは隠す
    this.header.hidden = name === "settings" || name === "about";
    this.callTitle.textContent = name === "call" ? this.channelLabel : "通話";
    this.shown = name;
  }

  private shown: ScreenName = "home";
  /** 設定・この通話についてから戻る先（通話中なら通話中へ）。 */
  private callActive = false;

  private back(): void {
    this.show(this.callActive ? "call" : "home");
  }

  private readonly createLabel = el("label");
  private readonly createLabelText = el("span", undefined, "チャンネルの名前");
  /** いま何を作っているか（入力欄を使い回すため）。 */
  private creating: "channel" | "group" = "channel";

  private openCreate(what: "channel" | "group"): void {
    this.creating = what;
    this.createLabelText.textContent = what === "group" ? "グループの名前" : "チャンネルの名前";
    this.createBox.hidden = false;
    this.createError.hidden = true;
    this.createInput.value = "";
    this.createInput.focus();
  }

  private closeCreate(): void {
    this.createBox.hidden = true;
    this.createError.hidden = true;
  }

  /** 「新しく作る」の検査が通らなかったときの文（`invite.ts` の検査の文をそのまま出す）。 */
  setCreateError(message: string | null): void {
    this.createError.hidden = message === null;
    if (message !== null) this.createError.textContent = message;
  }

  /** 作り終わった（入室できた）ので入力欄を畳む。 */
  createDone(): void {
    this.closeCreate();
  }

  // ---- 描き換え ----------------------------------------------------------

  setStatus(phase: Phase): void {
    this.badge.textContent = statusText(phase);
    this.badge.className =
      "badge" +
      (phase.kind === "ready" || phase.kind === "in-call"
        ? " ok"
        : phase.kind === "reconnecting"
          ? " warn"
          : phase.kind === "gave-up" || phase.kind === "stopped"
            ? " bad"
            : "");
    this.callActive = phase.kind === "in-call" || phase.kind === "reconnecting" || phase.kind === "gave-up";
    // 張り直し中だけは通話中の画面にも 1 行出す（画面仕様 §5）
    const show = phase.kind === "reconnecting" || phase.kind === "gave-up";
    this.callNotice.hidden = !show;
    if (show) this.callNotice.textContent = statusText(phase);
  }

  setName(name: string): void {
    this.nameInput.value = name;
  }

  /** 覚えているグループを並べる（**グループの一覧はサーバから来ない**。ADR-0019 §1-9）。 */
  setGroups(groups: readonly string[], current: string, labels: Readonly<Record<string, string>> = {}): void {
    this.groups.replaceChildren(
      ...groups.map((g) => {
        const b = button(labels[g] ?? g, "chip", () => this.h.onSelectGroup(g));
        b.setAttribute("aria-pressed", String(g === current));
        return b;
      }),
    );
    this.groups.hidden = groups.length <= 1;
  }

  /** チャンネルの一覧。**行は押すと選ぶだけ**（入室は下のボタン。iOS の都合で操作を起点にする）。 */
  setChannels(rows: readonly ChannelRow[], selected: string): void {
    this.selected = selected;
    this.list.replaceChildren(
      ...rows.map((r) => {
        const item = button("", "item" + (r.invited || r.recalled ? " ghost" : ""), () =>
          this.h.onSelectChannel(r.id),
        );
        item.setAttribute("aria-current", String(r.id === selected));
        const body = el("div", "body");
        body.append(el("div", "name", r.label));
        if (r.who) body.append(el("div", "who", r.who));
        item.replaceChildren(body, ...(r.count ? [el("span", "count", r.count)] : []));
        if (!r.recalled) return item;
        // 前にいた場所だけ、履歴から消す口を付ける（**チャンネルは消えない**）
        const wrap = el("div", "item ghost");
        wrap.replaceChildren(item, button("履歴から消す", "btn ghost", () => this.h.onForget(r.id)));
        item.className = "item";
        return wrap;
      }),
    );
  }

  /** ボタンの可否（`app-state.ts` が決めたものをそのまま受ける）。 */
  setButtons(can: {
    join: boolean;
    create: boolean;
    createGroup: boolean;
    /** グループの招待 URL をコピーできるか（**グループを選んでいるときだけ**）。 */
    groupInvite: boolean;
    leave: boolean;
    retry: boolean;
    mute: boolean;
  }): void {
    this.joinButton.disabled = !can.join;
    this.createButton.disabled = !can.create;
    // **チャンネルを作れないとき（所属グループが無い）はボタンを出さない。**
    // 押せないボタンを理由なしに並べない（文言で補わない決まり。画面仕様 §6-3）
    this.createButton.hidden = !can.create;
    this.createGroupButton.disabled = !can.createGroup;
    // **招待できるグループが無いときは出さない**（createButton と同じ扱い。画面仕様 §6-3）
    this.groupInviteButton.hidden = !can.groupInvite;
    if (!can.groupInvite) {
      this.setGroupInviteUrl("");
      this.setGroupCopyFeedback("");
    }
    this.leaveButton.disabled = !can.leave;
    this.retryButton.disabled = !can.retry;
    this.retryButton.hidden = !can.retry;
    this.muteButton.disabled = !can.mute;
  }

  /** 通話中の見出しに出すチャンネルの表示名。 */
  setCallTitle(label: string): void {
    this.channelLabel = label;
    if (this.shown === "call") this.callTitle.textContent = label;
  }

  private channelLabel = "通話";

  setMuted(muted: boolean): void {
    this.muteButton.textContent = muteLabel(muted);
    this.muteButton.setAttribute("aria-pressed", String(muted));
  }

  /** 参加者の行。**つまみを掴んでいる最中に作り直さない**ために、値だけ入れ替える。 */
  setPeople(rows: readonly PersonRow[], gains: ReadonlyMap<number, number>): void {
    const key = rows.map((r) => `${r.id}/${r.label}/${r.gain ? 1 : 0}`).join(",");
    if (key !== this.peopleKey) {
      this.peopleKey = key;
      this.peopleRows.clear();
      this.people.replaceChildren(
        ...rows.map((r) => {
          const row = el("div", "person");
          const name = el("span", "pname");
          row.append(name);
          let input: HTMLInputElement | null = null;
          let db: HTMLSpanElement | null = null;
          if (r.gain) {
            const s = slider(`${r.label} の声の大きさ`, RECEIVE_GAIN_MIN_DB, RECEIVE_GAIN_MAX_DB, 1, "dB");
            input = s.input;
            db = el("span", "db");
            input.value = String(gains.get(r.id) ?? 0);
            db.textContent = `${input.value} dB`;
            input.addEventListener("input", () => {
              db!.textContent = `${input!.value} dB`;
              this.h.onPersonGainDb(r.id, Number(input!.value), false);
            });
            // **離したときだけサーバへ送る**（制御ストリームが詰まる。画面仕様 §3）
            input.addEventListener("change", () => this.h.onPersonGainDb(r.id, Number(input!.value), true));
            row.append(input, db);
          }
          this.peopleRows.set(r.id, { row, name });
          return row;
        }),
      );
    }
    for (const r of rows) {
      const node = this.peopleRows.get(r.id);
      if (!node) continue;
      node.name.textContent = r.muted ? `🔇 ${r.label}` : r.label;
      node.row.classList.toggle("speaking", r.speaking);
    }
  }

  private peopleKey = "";
  private readonly peopleRows = new Map<number, { row: HTMLElement; name: HTMLElement }>();

  /** 入退室の知らせ（**音は鳴らさない**。画面仕様 §4）。 */
  setLogLine(text: string): void {
    this.logLine.hidden = text === "";
    this.logLine.textContent = text;
  }

  setGains(masterDb: number, sendDb: number): void {
    this.master.input.value = String(masterDb);
    this.master.input.dispatchEvent(new Event("input"));
    this.send.input.value = String(sendDb);
    this.send.input.dispatchEvent(new Event("input"));
  }

  /**
   * 音声検出のゲージ（画面仕様 §6-1。**置き場は設定の画面だけ**）。
   *
   * **いまの入力の棒はコアから来る**（`Engine::input_level_db`。平滑化済み。タスク #178）。
   * 省略できる形は残してある — 入力レベルを持たない古い WASM でも画面が止まらないように
   * （その場合は静かなときの音量と効いている境目の 2 つだけが出る）。
   */
  setGauge(floorDb: number, thresholdDb: number, levelDb?: number): void {
    this.gaugeFloor.style.width = `${dbToPercent(floorDb)}%`;
    this.gaugeThreshold.style.left = `${dbToPercent(thresholdDb)}%`;
    this.gaugeLevel.hidden = levelDb === undefined;
    if (levelDb !== undefined) this.gaugeLevel.style.width = `${dbToPercent(levelDb)}%`;
  }

  /** 送る音の範囲。上限はチャンネルの設定で変わる（`max_lowpass_hz`）。 */
  setBand(highpassHz: number, lowpassHz: number, maxLowpassHz: number): void {
    this.lowpass.input.max = String(Math.round(maxLowpassHz));
    this.highpass.input.value = String(Math.round(highpassHz));
    this.lowpass.input.value = String(Math.round(Math.min(lowpassHz, maxLowpassHz)));
    this.highpass.input.dispatchEvent(new Event("input"));
    this.lowpass.input.dispatchEvent(new Event("input"));
  }

  /** 失敗・拒否の案内（`failure-notice.ts` の文言をそのまま出す）。 */
  setNotice(notice: Notice | null, detail = ""): void {
    this.homeNotice.hidden = notice === null;
    if (!notice) return;
    this.homeNotice.replaceChildren(el("b", undefined, notice.head));
    if (notice.action) this.homeNotice.append(document.createTextNode(notice.action));
    if (notice.detail && detail) this.homeNotice.append(el("p", "note", detail));
  }

  /** コピーの手応え（4 秒だけ出す。失敗は赤）。 */
  setCopyFeedback(text: string, bad = false): void {
    this.copyFeedback.hidden = text === "";
    this.copyFeedback.textContent = text;
    this.copyFeedback.style.color = bad ? "var(--bad)" : "";
  }

  /**
   * コピーに失敗する端末のために URL を見せる（画面仕様 §3）。
   *
   * `label` を付けると**何の招待 URL か**を頭に出す。**グループを作った直後**に、
   * チャンネル宛ではなくグループ宛を見せるために使う（2026-10-29。通話中の画面には
   * ボタンを増やさない決定なので、文字で区別する）。
   */
  setInviteUrl(url: string, label = ""): void {
    this.inviteUrl.hidden = url === "";
    if (label === "") {
      this.inviteUrl.textContent = url;
      return;
    }
    this.inviteUrl.replaceChildren(el("b", undefined, `${label} `), document.createTextNode(url));
  }

  /** 入室前の画面に出すグループの招待 URL（コピーに失敗する端末のため。画面仕様 §3）。 */
  setGroupInviteUrl(url: string): void {
    this.groupInviteUrl.hidden = url === "";
    this.groupInviteUrl.textContent = url;
  }

  /** グループの招待 URL のコピーの手応え（4 秒だけ出す。失敗は赤）。 */
  setGroupCopyFeedback(text: string, bad = false): void {
    this.groupCopyFeedback.hidden = text === "";
    this.groupCopyFeedback.textContent = text;
    this.groupCopyFeedback.style.color = bad ? "var(--bad)" : "";
  }

  setVersionLine(text: string): void {
    this.versionLine.textContent = text;
  }

  /** いま選んでいる行き先（配線が入室に使う）。 */
  get selectedChannel(): string {
    return this.selected;
  }

  /** いま出している画面。 */
  get current(): ScreenName {
    return this.shown;
  }
}
